import React, { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { RecruitmentStatusType } from "../../../configs/constants/RecruitmentStatusType";
import { cn } from "../../../utils/cn";
import PoolFilter from "../PoolFilter/PoolFilter";

export interface BreadcrumbItem {
	title: string | React.ReactNode;
	path: string;
}

interface BreadcrumbsProps {
	items?: BreadcrumbItem[];
	statusItem?: {
		status: string;
		action: any;
	};

	showPoolDropdown?: boolean;

	onChangePool?: (pool: string) => any;

	selectedPool?: string;

	/**
	 * When true (default) the bar cancels any padding applied by its parent so it
	 * always sits flush against the top and side edges of the scroll container —
	 * regardless of whether the page wraps it in a padded content div. Set to
	 * false to let it flow inline with the parent's padding.
	 */
	fullBleed?: boolean;

	className?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
	items,
	statusItem,
	className,
	showPoolDropdown,
	onChangePool,
	selectedPool,
	fullBleed = true,
}) => {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const ref = useRef<HTMLDivElement>(null);

	// Neutralise padding inherited from the parent so the bar is always flush to
	// the top/side edges. Some pages render the breadcrumbs inside a padded
	// content container (e.g. `p-4 px-6`), which would otherwise inset the bar
	// and expose the page background around it. We read the parent's computed
	// padding and offset it with matching negative margins, re-measuring whenever
	// the parent resizes (covers responsive padding at breakpoints).
	useLayoutEffect(() => {
		const el = ref.current;
		const parent = el?.parentElement;
		if (!el || !parent) return;

		if (!fullBleed) {
			el.style.marginTop = "";
			el.style.marginLeft = "";
			el.style.marginRight = "";
			return;
		}

		const apply = () => {
			const cs = window.getComputedStyle(parent);
			el.style.marginTop = `-${parseFloat(cs.paddingTop) || 0}px`;
			el.style.marginLeft = `-${parseFloat(cs.paddingLeft) || 0}px`;
			el.style.marginRight = `-${parseFloat(cs.paddingRight) || 0}px`;
		};

		apply();

		const observer = new ResizeObserver(apply);
		observer.observe(parent);
		return () => observer.disconnect();
	}, [fullBleed]);

	const getStatusBgColor = (status: string) => {
		switch (status) {
			case RecruitmentStatusType.HIRED:
				return "bg-success";
			case RecruitmentStatusType.CANCELLED:
				return "bg-danger";
			default:
				return "bg-action";
		}
	};

	return (
		<div
			ref={ref}
			className={cn("flex h-[32px] items-center border-b border-gray-300 px-4 pl-5", className)}
		>
			<div className="flex w-full justify-between">
				<div className="flex items-center gap-1.5 text-[13px]">
					{items &&
						items.map((br, index) => (
							<React.Fragment key={index}>
								{index > 0 && <span className="text-[#888]">/</span>}
								<span
									className={cn(
										"cursor-pointer font-medium",
										index === 0
											? "font-semibold text-[#333]"
											: index === items.length - 1
											? "text-[#888]"
											: "text-[#333]"
									)}
									onClick={() => navigate(br.path)}
								>
									{typeof br.title === "string" ? t((br.title || "") as any) : br.title}
								</span>
							</React.Fragment>
						))}
				</div>
				{statusItem && (
					<span
						className={cn(
							"flex cursor-pointer flex-row items-center justify-center whitespace-nowrap rounded-[3px] px-[5px] py-[3px] text-[0.9rem] font-semibold text-white",
							getStatusBgColor(statusItem.status)
						)}
						onClick={() => statusItem.action()}
					>
						{t(statusItem.status as any)}
					</span>
				)}
				{showPoolDropdown && onChangePool && (
					<PoolFilter value={selectedPool} onChange={onChangePool} />
				)}
			</div>
		</div>
	);
};

export default Breadcrumbs;
